import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';

import { AuthService } from '@app/services/auth/auth.service';

/** Small helper so native (token) + web (session cookie) both work */
function isAuthenticated(auth: AuthService): boolean {
  // 📱 Native: token is present
  // 🌐 Web:    token may be null, but user will be set from /account/me
  return !!auth.user || !!auth.token;
}

/** Wait for AuthService to bootstrap before checking anything */
async function waitForAuth(auth: AuthService) {
  await auth.ready;
  if (typeof (auth as any).whenReady === 'function') {
    await (auth as any).whenReady();
  }
}

/** Allow only authenticated users. If not, go to /signin */
export const authGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const auth = inject(AuthService);
  const router = inject(Router);

  await waitForAuth(auth);

  // console.log('[authGuard] token:', auth.token);
  // console.log('[authGuard] user:', auth.user);

  const authed = !!auth.user || !!auth.token;
  // console.log("🚀 ~ auth-guard.ts:32 ~ authGuard ~ authed:", authed)

  return authed ? true : router.parseUrl('/signin');
};

/** Allow only authed users with a given application stage */
export const stageGuard = (allowedStages: string[]): CanActivateFn => {
  return async (): Promise<boolean | UrlTree> => {
    const auth = inject(AuthService);
    const router = inject(Router);

    await waitForAuth(auth);
    if (!isAuthenticated(auth)) {
      return router.parseUrl('/signin');
    }

    let stage = auth.user?.application_stage;
    let cascadeStatus = auth.user?.cascade_status?.trim().toLowerCase();
    if( cascadeStatus !== 'active' && cascadeStatus != null  ){
      stage = cascadeStatus; //overwrite so we drop into unkown account status
    }
   //console.log("🚀 ~ auth-guard.ts:53 ~ stageGuard ~ stage:", stage)


    if (stage && allowedStages.includes(stage)) {
      return true;
    }

    // If stage not allowed, send them somewhere sensible
    return router.parseUrl('/signin'); // or '/' or '/apply' etc.
  };
};


/** Allow only guests (not authenticated). If authed, go to home / */
export const guestGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const auth = inject(AuthService);
  const router = inject(Router);
  await waitForAuth(auth);

  return isAuthenticated(auth) ? router.parseUrl('/') : true;
};

/** Allow only authed users with a given channel (e.g. Alpha) */
export const channelGuard = (channelName: string): CanActivateFn => {
  return async (): Promise<boolean | UrlTree> => {
    const auth = inject(AuthService);
    const router = inject(Router);

    await waitForAuth(auth);

    if (!isAuthenticated(auth)) {
      return router.parseUrl('/signin');
    }

    const hasChannel = await auth.hasChannel(channelName);
    return hasChannel ? true : router.parseUrl('/signin');
  };
};


export const homeRedirectGuard: CanActivateFn = async (): Promise<UrlTree> => {
  const auth = inject(AuthService);
  const router = inject(Router);

  await waitForAuth(auth);

  // Not logged in → go to signin
  if (!isAuthenticated(auth)) {
    return router.parseUrl('/signin');
  }

  let stage = auth.user?.application_stage?.trim().toLowerCase();
  const cascadeStatus = auth.user?.cascade_status?.trim().toLowerCase();

  if ( //all these stages are grouped into applicant
    stage === 'interest' ||
    stage === 'training call' ||
    stage === 'test assessment' ||
    stage === 'completed test assessment'
  ) {
    stage = 'applicant';
  }
  

  if( cascadeStatus !== 'active' && cascadeStatus != null  ){
    stage = cascadeStatus; // overwrite so unmapped account statuses use the default view
    // console.log("🚀 ~ auth-guard.ts:119 ~ homeRedirectGuard ~ cascadeStatus:", cascadeStatus)
    
  }

  // console.log("🚀 ~ auth-guard.ts:123 ~ homeRedirectGuard ~ cascadeStatus:", cascadeStatus)
  // console.log("🚀 ~ auth-guard.ts:124 ~ homeRedirectGuard ~ auth.user:", auth.user)
  console.log("🚀 ~ auth-guard.ts:125 ~ homeRedirectGuard ~ stage:", stage)
 

  

  switch (stage) {
    case 'assessor':
      return router.parseUrl('/home');
    case 'applying':
      return router.parseUrl('/applying');
    case 'applicant':
      return router.parseUrl('/applicant');
    case 'rejected':
      return router.parseUrl('/application-status');
    default:
      // IMPORTANT: authed fallback must NOT be /signin
      return router.parseUrl('/account-status'); // or /application-status
  }

};

export const permissionGuard = (permissionName: string): CanActivateFn => {
  return async (): Promise<boolean | UrlTree> => {
    const auth = inject(AuthService);
    const router = inject(Router);

    await waitForAuth(auth);

    // Must be logged in
    if (!isAuthenticated(auth)) {
      return router.parseUrl('/signin');
    }

    const permissions = auth.user?.permissions ?? {};
    const hasPermission = !!(permissions as any)[permissionName];

    // console.log('[permissionGuard] checking', permissionName, '=>', hasPermission);

    return hasPermission ? true : router.parseUrl('/signin');
  };
};


